// src/components/panel/brandlist.js
import axios from 'axios';
import { SiHomeassistantcommunitystore } from "react-icons/si";
import { FaSort, FaSortAlphaDown, FaSortAlphaDownAlt, FaSortNumericDown, FaSortNumericDownAlt } from "react-icons/fa";
import { PiCalendarDotsDuotone, PiCalendarDotsThin } from "react-icons/pi";
import React, { useState, useEffect } from 'react';
import { NODE_BACKEND_API_URL } from '../../config';
import './panel.css';

function BrandList() {
    // State for the list of brands and product quantities
    const [brandData, setBrandData] = useState([]);
    // State for the total number of brands (derived from brandData length)
    const [totalBrands, setTotalBrands] = useState(0);
    // State for loading and error handling
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // State for sorting
    const [sortConfig, setSortConfig] = useState({
        key: 'BrandName', // Default sort by brand name
        direction: 'asc' // Default ascending (A-Z)
    });

    // Function to format date to DD/MM/YY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const year = String(date.getFullYear()).slice(-2); // Get last two digits of the year
        return `${day}/${month}/${year}`;
    };

    // Function to get update status color based on date difference
    const getUpdateStatusColor = (dateString) => {
        if (!dateString) return 'var(--color-status-critical)'; // Red for no date
        
        const lastUpdate = new Date(dateString);
        const currentDate = new Date();
        const diffTime = Math.abs(currentDate - lastUpdate);
        const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.44); // Average days per month
        
        if (diffMonths > 3) {
            return 'var(--color-status-critical)'; // Red - more than 3 months
        } else if (diffMonths >= 1) {
            return 'var(--color-status-warning)'; // Yellow - 1 to 3 months
        } else {
            return 'var(--color-status-good)'; // Green - less than 1 month
        }
    };

    // Function to get update status circle
    const getUpdateStatusCircle = (dateString) => {
        const color = getUpdateStatusColor(dateString);
        return (
            <span 
                style={{
                    display: 'inline-block',
                    width: '1em',
                    height: '1em',
                    borderRadius: '50%',
                    backgroundColor: color,
                    marginRight: '8px',
                    verticalAlign: 'middle'
                }}
                title={
                    !dateString ? 'Sin fecha de actualización' :
                    color === '#ff0000' ? 'Más de 3 meses sin actualizar' :
                    color === '#ffaa00' ? 'Entre 1 y 3 meses sin actualizar' :
                    'Actualizado recientemente (menos de 1 mes)'
                }
            />
        );
    };

    // Function to handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Function to get sort indicator
    const getSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <FaSort style={{ marginLeft: '5px', fontSize: '12px', opacity: 0.5 }} />; // Default sort icon
        }
        
        // Return specific icons based on column and direction
        switch (columnKey) {
            case 'BrandName':
                return sortConfig.direction === 'asc' 
                    ? <FaSortAlphaDown style={{ marginLeft: '5px', fontSize: '12px' }} />
                    : <FaSortAlphaDownAlt style={{ marginLeft: '5px', fontSize: '12px' }} />;
            case 'NumberOfProducts':
                return sortConfig.direction === 'asc' 
                    ? <FaSortNumericDown style={{ marginLeft: '5px', fontSize: '12px' }} />
                    : <FaSortNumericDownAlt style={{ marginLeft: '5px', fontSize: '12px' }} />;
            case 'LastUpdate':
                return sortConfig.direction === 'asc' 
                    ? <PiCalendarDotsDuotone style={{ marginLeft: '5px', fontSize: '14px' }} />
                    : <PiCalendarDotsThin style={{ marginLeft: '5px', fontSize: '14px' }} />;
            default:
                return <FaSort style={{ marginLeft: '5px', fontSize: '12px', opacity: 0.5 }} />;
        }
    };

    // Function to sort data
    const getSortedData = () => {
        if (!brandData || brandData.length === 0) return [];
        
        const sortedData = [...brandData].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'BrandName':
                    aValue = (a.BrandName || '').toLowerCase();
                    bValue = (b.BrandName || '').toLowerCase();
                    break;
                case 'NumberOfProducts':
                    aValue = parseInt(a.NumberOfProducts) || 0;
                    bValue = parseInt(b.NumberOfProducts) || 0;
                    break;
                case 'LastUpdate':
                    aValue = a.LastUpdate ? new Date(a.LastUpdate) : new Date(0);
                    bValue = b.LastUpdate ? new Date(b.LastUpdate) : new Date(0);
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        
        return sortedData;
    };

    useEffect(() => {
        // Define the API endpoint for fetching brand quantities
        // This endpoint returns the list of brands and their product counts
        const brandQuantitiesUrl = `${NODE_BACKEND_API_URL}/getbrandquantities`;

        // Fetch data from the single endpoint
        axios.get(brandQuantitiesUrl)
            .then(res => {
                // --- ADD THIS LOG ---
                console.log("Data received from /getbrandquantities:", res.data);
                // --- END ADD THIS LOG ---

                // Assuming getbrandquantities returns an array of objects
                // like [{ BrandName: ..., NumberOfProducts: ..., LastUpdate: ... }, ...]

                // Set the brand data state with the received array
                setBrandData(res.data);

                // Calculate the total number of brands from the length of the received array
                if (Array.isArray(res.data)) {
                    setTotalBrands(res.data.length);
                } else {
                    // Handle unexpected response format if needed
                    console.warn("Expected array from /getbrandquantities, but received:", res.data);
                    setTotalBrands(0); // Set total to 0 if data is not an array
                }

                setLoading(false); // Set loading to false after the call completes successfully
            })
            .catch(er => {
                console.error("Error fetching brand data:", er); // Log the actual error
                setError("Error al cargar los datos de marcas."); // Set a user-friendly error message
                setLoading(false); // Set loading to false on error
            });

    }, []); // Empty dependency array means this effect runs once on mount

    // Render loading, error, or data based on state
    if (loading) {
        return <div className='cardM'>Cargando datos de marcas...</div>;
    }

    if (error) {
        return <div className='cardM'>{error}</div>;
    }

    // Check if brandData is available and not empty before rendering the table
    if (!brandData || brandData.length === 0) {
        return <div className='cardM'>No se encontraron datos de marcas.</div>;
    }

    return (
        <div className='cardM'>
            <div className='card-innerM'>
                <SiHomeassistantcommunitystore className='card_iconM' size={38} />
                {/* Display TotalBrands state */}
                <h2>MARCAS: {totalBrands}</h2>
            </div>
            <br></br>
            <div className='panel-blue'>
                <table className='tablamarcas'>
                    <thead className='th-panel-blue'>
                        <tr>
                            <th 
                                className='th-brand-name' 
                                onClick={() => handleSort('BrandName')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                title="Hacer clic para ordenar por marca"
                            >
                                MARCA{getSortIndicator('BrandName')}
                            </th>
                            <th 
                                className='th-brand-prod-count' 
                                onClick={() => handleSort('NumberOfProducts')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                title="Hacer clic para ordenar por número de productos"
                            >
                                #PROD{getSortIndicator('NumberOfProducts')}
                            </th>
                            <th 
                                className='th-last-update' 
                                onClick={() => handleSort('LastUpdate')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                title="Hacer clic para ordenar por fecha de actualización"
                            >
                                ACTUALIZADO{getSortIndicator('LastUpdate')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            // Use sorted data instead of original brandData
                            getSortedData().map((item, index) => (
                                // Using a unique ID from the data, if available, is preferred as a key
                                // Using index as key is acceptable here if list order is static and no items are added/removed/reordered
                                <tr className='brand-list-table-row' key={item.BrandName || index}> {/* Using BrandName as key if available, fallback to index */}
                                    <td className='td-Brand'>{item.BrandName}</td>
                                    <td className='td-Prod'>{item.NumberOfProducts}</td>
                                    {/* Display LastUpdate value with color-coded circle and formatted date */}
                                    <td className='td-LastUpdate'>
                                        {getUpdateStatusCircle(item.LastUpdate)}
                                        {formatDate(item.LastUpdate)}
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default BrandList;